import * as React from "react";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";

import { cn } from "../../utils";
import { ButtonProps, buttonVariants } from "./Button";
import { Svg } from "./Svg";

const Pagination = ({
  className,
  ...props
}: React.ComponentProps<"nav"> & { className?: string }) => (
  <nav
    
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    data-testid="pagination"
    {...props}
  />
);
Pagination.displayName = "Pagination";

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul"> & { className?: string }
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center", className)}
    {...props}
  />
));
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li"> & { className?: string }
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn(
      "border-y border-r first:rounded-l-xl first:border-l last:rounded-r-xl",
      className
    )}
    {...props}
  />
));
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, "size"> &
  React.ComponentProps<"a">;

const PaginationLink = ({
  children,
  className,
  isActive,
  size = "icon",
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? "blue" : "ghost",
        size,
      }),
      "rounded-none",
      "hover:cursor-pointer",
      className
    )}
    {...props}
  >
    {children}
  </a>
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { className?: string }) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn("gap-1 rounded-l-xl pl-2.5 pr-5", className)}
    {...props}
  >
    <Svg name="arrow_left" alt="Previous" />
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { className?: string }) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn("gap-1 rounded-r-xl pl-5 pr-2.5", className)}
    {...props}
  >
    <Svg name="arrow_right" alt="Next" />
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span"> & { className?: string }) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <DotsHorizontalIcon className="mt-3 size-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
